/**
 * Draft Storage Service for Ameen Insurance Assistant
 *
 * Manages saving and loading of in-progress policy/claim drafts via the .NET
 * backend API, enabling users to resume flows from where they left off.
 * Drafts are persisted server-side, linked to the authenticated user.
 */

import { FlowStep, TransitionData } from '../agents/types';
import { apiFetch } from '../utils/api';

// ─── Types ───────────────────────────────────────────────────────────────────

export type DraftFlowType = 'policy' | 'claim';

/**
 * A saved draft profile representing an in-progress flow.
 */
export interface DraftProfile {
    /** Unique UUID (generated client-side, used as DraftClientId on the backend) */
    id: string;
    /** Which flow this draft belongs to */
    flowType: DraftFlowType;
    /** The agent the user was on when saved */
    currentAgentId: string;
    /** The UI flow step the user was on */
    currentFlowStep: FlowStep;
    /** Arabic label for the last completed step (shown on the card) */
    lastStepLabel: string;
    /** Timestamp (Date.now()) */
    savedAt: number;
    /** Owner's mobile number (kept for display / backwards-compat) */
    mobileNumber: string;

    // ── Snapshot of collected data ──

    /** Full transition data at the time of save */
    transitionData: TransitionData;
    /** OCR-extracted identity/vehicle data */
    customerNationalIdData?: any;
    /** List of completed document step names, e.g. ['national_id_front', 'national_id_back'] */
    completedDocuments?: string[];
    /** Underwriting answers collected so far: { vehicle_usage: 'personal', ... } */
    underwritingAnswers?: Record<string, any>;
    /** Vehicle survey/inspection images */
    surveyImages?: any[];
    /** Claim policy inquiry result */
    claimPolicyData?: any;
    /** Premium calculation results (companies/packages) */
    companiesData?: any[];
    /** User-selected insurance package */
    selectedPackage?: any;
    /** Conversation summary for context injection */
    conversationSummary?: string;
}

// ─── Step Labels (Arabic) ────────────────────────────────────────────────────

/**
 * Maps (agentId, flowStep) to an Arabic label describing the last completed action.
 * Used on draft cards in the UI.
 */
const STEP_LABELS: Record<string, Record<string, string>> = {
    policy_agent: {
        package_selection: 'تم حساب عروض الأسعار',
        idle: 'تم اختيار الباقة',
    },
    document_agent: {
        national_id_front: 'بداية رفع المستندات',
        national_id_back: 'تم رفع البطاقة الأمامية',
        driving_license: 'تم رفع البطاقة الخلفية',
        vehicle_license_front: 'تم رفع رخصة القيادة',
        vehicle_license_back: 'تم رفع رخصة السيارة (أمام)',
        idle: 'تم رفع رخصة السيارة (خلف)',
    },
    under_writing_question_agent: {
        underwriting_vehicle_usage: 'بداية أسئلة الاكتتاب',
        underwriting_had_accident: 'تم الإجابة: استخدام السيارة',
        underwriting_night_parking: 'تم الإجابة: حوادث سابقة',
        underwriting_violations: 'تم الإجابة: مكان الركن ليلاً',
        underwriting_tracking_device: 'تم الإجابة: المخالفات المرورية',
        underwriting_vehicle_financing: 'تم الإجابة: جهاز التتبع',
        underwriting_number_of_drivers: 'تم الإجابة: تمويل السيارة',
        idle: 'تم الإجابة: عدد السائقين',
    },
    vehicle_inspection_agent: {
        vehicle_survey: 'بداية فحص السيارة',
        vehicle_upload: 'جاري فحص السيارة',
        idle: 'تم فحص السيارة',
    },
    claim_agent: {
        claim_policy_inquiry: 'بداية استعلام عن الوثيقة',
        idle: 'تم التحقق من الوثيقة',
    },
    submit_claim_agent: {
        claim_workshop_options: 'اختيار الورشة',
        claim_contracted_workshops: 'اختيار ورشة متعاقدة',
        claim_non_contracted_form: 'إدخال ورشة غير متعاقدة',
        idle: 'تم اختيار الورشة',
    },
};

/**
 * Get the Arabic label for a given agent + flow step.
 */
export function getStepLabel(agentId: string, flowStep: FlowStep): string {
    return STEP_LABELS[agentId]?.[flowStep] ?? 'قيد التنفيذ';
}

// ─── UUID Generator ──────────────────────────────────────────────────────────

function generateId(): string {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) {
        return crypto.randomUUID();
    }
    return 'xxxx-xxxx-xxxx-xxxx'.replace(/x/g, () =>
        Math.floor(Math.random() * 16).toString(16)
    );
}

// ─── Backend API Helpers ─────────────────────────────────────────────────────

/** Shape of the backend SaveDraftRequest */
interface SaveDraftRequest {
    draftClientId: string;
    flowType: string;
    currentAgentId: string;
    currentFlowStep: string;
    lastStepLabel: string;
    savedAt: number;
    draftDataJson: string;
}

/** Shape of the backend UserSessionDraftResponse items */
interface BackendDraftResponse {
    id: number;
    draftClientId: string;
    flowType: string;
    currentAgentId: string;
    currentFlowStep: string;
    lastStepLabel: string;
    savedAt: number;
    status: string;
    draftDataJson: string;
    creationTime: string;
    updateTime?: string;
}

// ─── Public API ──────────────────────────────────────────────────────────────

/**
 * Save (upsert) a draft to the backend.
 * Fire-and-forget; logs errors but does not throw.
 */
export async function saveDraft(draft: DraftProfile): Promise<void> {
    try {
        const payload: SaveDraftRequest = {
            draftClientId: draft.id,
            flowType: draft.flowType,
            currentAgentId: draft.currentAgentId,
            currentFlowStep: draft.currentFlowStep,
            lastStepLabel: draft.lastStepLabel,
            savedAt: draft.savedAt,
            draftDataJson: JSON.stringify(draft),
        };

        await apiFetch('/UserSessionDraft/save', {
            method: 'POST',
            body: JSON.stringify(payload),
        });

        console.log(`[DraftStorage] Draft saved: ${draft.flowType} / ${draft.currentAgentId} / ${draft.currentFlowStep}`);
    } catch (e) {
        console.error('[DraftStorage] Failed to save draft:', e);
    }
}

/**
 * Get the count of InProgress drafts for the current user (lightweight, no data).
 * Uses the fast `my-draft-count` endpoint that avoids loading DraftDataJson blobs.
 */
export async function getDraftCount(): Promise<number> {
    try {
        const response = await apiFetch('/UserSessionDraft/my-draft-count');
        if (!response.ok) return 0;

        const data = await response.json();
        return data.count ?? 0;
    } catch (e) {
        console.error('[DraftStorage] Failed to get draft count:', e);
        return 0;
    }
}

/**
 * Get all InProgress drafts for the current authenticated user.
 * The mobileNumber parameter is kept for backwards-compat but is not used for filtering
 * (the backend filters by the JWT user identity).
 * Handles both the new paginated response { items, totalCount } and the legacy flat array.
 */
export async function getDraftsForUser(_mobileNumber: string): Promise<DraftProfile[]> {
    try {
        const response = await apiFetch('/UserSessionDraft/my-drafts');
        if (!response.ok) return [];

        const json = await response.json();

        // Support both paginated { items: [...] } and legacy flat array
        const backendDrafts: BackendDraftResponse[] = Array.isArray(json) ? json : (json.items ?? []);

        return backendDrafts.map((item) => {
            try {
                const parsed: DraftProfile = JSON.parse(item.draftDataJson);
                // Ensure the id always matches the backend client id
                parsed.id = item.draftClientId;
                return parsed;
            } catch {
                // Fallback: reconstruct a minimal DraftProfile from metadata
                return {
                    id: item.draftClientId,
                    flowType: item.flowType as DraftFlowType,
                    currentAgentId: item.currentAgentId,
                    currentFlowStep: item.currentFlowStep as FlowStep,
                    lastStepLabel: item.lastStepLabel,
                    savedAt: item.savedAt,
                    mobileNumber: _mobileNumber,
                    transitionData: {} as TransitionData,
                };
            }
        });
    } catch (e) {
        console.error('[DraftStorage] Failed to load drafts:', e);
        return [];
    }
}

/**
 * Mark a draft as Completed by its client UUID.
 * Used both on flow completion (auto) and when the user manually dismisses a card.
 */
export async function completeDraft(draftClientId: string): Promise<void> {
    try {
        await apiFetch(`/UserSessionDraft/complete/${encodeURIComponent(draftClientId)}`, {
            method: 'POST',
        });
        console.log(`[DraftStorage] Draft completed: ${draftClientId}`);
    } catch (e) {
        console.error('[DraftStorage] Failed to complete draft:', e);
    }
}

// ─── Draft Builder Helper ────────────────────────────────────────────────────

/**
 * Create or update a draft profile snapshot from the current flow state.
 * If `existingDraftId` is provided, it reuses that ID (update). Otherwise creates new.
 */
export function buildDraftSnapshot(params: {
    existingDraftId?: string;
    flowType: DraftFlowType;
    currentAgentId: string;
    currentFlowStep: FlowStep;
    mobileNumber: string;
    transitionData: TransitionData;
    customerNationalIdData?: any;
    completedDocuments?: string[];
    underwritingAnswers?: Record<string, any>;
    surveyImages?: any[];
    claimPolicyData?: any;
    companiesData?: any[];
    selectedPackage?: any;
    conversationSummary?: string;
}): DraftProfile {
    return {
        id: params.existingDraftId || generateId(),
        flowType: params.flowType,
        currentAgentId: params.currentAgentId,
        currentFlowStep: params.currentFlowStep,
        lastStepLabel: getStepLabel(params.currentAgentId, params.currentFlowStep),
        savedAt: Date.now(),
        mobileNumber: params.mobileNumber,
        transitionData: params.transitionData,
        customerNationalIdData: params.customerNationalIdData,
        completedDocuments: params.completedDocuments,
        underwritingAnswers: params.underwritingAnswers,
        surveyImages: params.surveyImages,
        claimPolicyData: params.claimPolicyData,
        companiesData: params.companiesData,
        selectedPackage: params.selectedPackage,
        conversationSummary: params.conversationSummary,
    };
}
